// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/';
import { DashboardDropWidgetResult, WIDGET_DND_TYPES } from '@Types';
import React, { ReactElement, useMemo, useRef } from 'react';
import { useDrop } from 'react-dnd';
import EmptyElement from '@Organisms/Dashboard/DashboardDetails/EmptyElementToDrop/EmptyElementToDrop';
import { WidgetOutput } from 'thehive-sdk';
import DropableWidgetView from '@Organisms/Dashboard/DashboardDetails/DropableWidgetView/DropableWidgetView';
import { WidgetView } from '@Organisms/Dashboard/DashboardDetails/WidgetView/WidgetView';
import { WidgetQuery } from '@Context/DashboardContext';

import './DropableWidgetViewList.scss';

interface DropableWidgetViewListProps {
    rowIndex: number;
    canAddWidget: boolean;
    widgetList: WidgetOutput[];
    onDragFinish: (dropResult: DashboardDropWidgetResult, originWidgetIndex: number, widgetData: WidgetOutput) => void;
    className?: string;
    widgets: WidgetQuery[];
}

const DropableWidgetViewList = ({
    rowIndex,
    canAddWidget,
    widgetList,
    onDragFinish,
    widgets,
    className = '',
}: DropableWidgetViewListProps): ReactElement => {
    const ref = useRef(null);
    const i = useLocalMessage();

    const [{ isHovered, isOverCurrent, canDrop }, drop] = useDrop({
        accept: WIDGET_DND_TYPES.WIDGET,
        collect: (monitor) => ({
            isHovered: monitor.isOver(),
            isOverCurrent: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
        canDrop: (item: any) => canAddWidget || item?.draggedRowIndex === rowIndex,
        drop: (item, monitor) => {
            const dropResult = monitor.getDropResult() || {};
            return { ...dropResult, hoveredRowIndex: rowIndex };
        },
    });

    drop(ref);

    const shouldDisplayEmptyBlock = isHovered && isOverCurrent && canDrop;

    const list = useMemo(
        () =>
            widgetList.map((widget, index) => {
                const widgetQuery = widgets.find(({ widget: { id } }) => id === widget.id);

                return (
                    <DropableWidgetView
                        rowIndex={rowIndex}
                        canAddWidget={canAddWidget}
                        widgetIndex={index}
                        onDragFinish={(dropResult) => onDragFinish(dropResult, index, widget)}
                        key={`div-item-${widget.id}`}
                    >
                        <WidgetView
                            widget={widget}
                            key={`div-item-${widget.id}`}
                            query={widgetQuery?.query}
                            className={className}
                        />
                    </DropableWidgetView>
                );
            }),
        [widgetList, widgets, canAddWidget, onDragFinish, rowIndex, className],
    );

    return (
        <div ref={ref} className="dropable-widget-view-list">
            {list}
            {shouldDisplayEmptyBlock && <EmptyElement text={i('dashboard.drop.widget')} />}
        </div>
    );
};

export default DropableWidgetViewList;
